import { useCallback } from 'react';
import type { AnalyticsEvent, PurchaseEvent } from '../api/types';

declare global {
  interface Window {
    gtag?: (
      command: string,
      eventName: string,
      params?: Record<string, unknown>
    ) => void;
    uetq?: Array<unknown>;
    dataLayer?: Array<unknown>;
  }
}

export const useAnalytics = () => {
  // Track GA4 event
  const trackEvent = useCallback((event: AnalyticsEvent) => {
    if (typeof window.gtag === 'function') {
      const { event: eventName, ...params } = event;
      window.gtag('event', eventName, params);
      console.log('[GA4] Event tracked:', eventName, params);
    } else {
      console.warn('[GA4] gtag not available');
    }
  }, []);

  // Track page view
  const trackPageView = useCallback((path: string, title?: string) => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: path,
        page_title: title || document.title,
      });
      console.log('[GA4] Page view tracked:', path);
    }
  }, []);

  // Track purchase event (GA4 + Universal Analytics)
  const trackPurchase = useCallback((purchase: PurchaseEvent) => {
    if (window.dataLayer) {
      // Clear previous ecommerce object (GA4 best practice)
      window.dataLayer.push({ ecommerce: null });

      // Push GA4 purchase event
      window.dataLayer.push({
        event: 'purchase',
        ecommerce: purchase,
      });
      console.log('[GA4] Purchase tracked:', purchase);

      // Push Universal Analytics purchase event (legacy support)
      const uaEvent = {
        event: 'event',
        eventCategory: 'Ecommerce',
        eventAction: 'Purchase-GTM',
        eventLabel: Math.random().toString(36).substr(2, 5),
        eventNonInteraction: false,
        ecommerce: {
          purchase: {
            actionField: {
              id: purchase.transaction_id,
              revenue: purchase.value,
              tax: '0',
              shipping: '0',
              coupon: purchase.coupon || '',
            },
            products: purchase.items.map((item) => ({
              name: item.item_name,
              id: item.item_id,
              price: item.price,
              variant: item.item_variant || '',
              quantity: item.quantity,
            })),
          },
        },
      };
      window.dataLayer.push(uaEvent);
      console.log('[UA] Purchase tracked:', uaEvent);
    } else {
      console.warn('[Analytics] dataLayer not available');
    }
  }, []);

  // Track Microsoft UET event
  const trackUetEvent = useCallback(
    (
      eventType: 'purchase' | 'page_load' | 'custom',
      params?: Record<string, unknown>
    ) => {
      if (window.uetq) {
        const event: Record<string, unknown> = {
          ec: eventType,
          ...params,
        };
        window.uetq.push(event);
        console.log('[UET] Event tracked:', event);
      } else {
        console.warn('[UET] uetq not available');
      }
    },
    []
  );

  // Track UET purchase
  const trackUetPurchase = useCallback(
    (revenue: number, currency: string = 'USD') => {
      if (window.uetq) {
        // Ensure revenue is a number and valid
        const revenueValue = Number(revenue);

        // Validate that we have a valid number
        if (isNaN(revenueValue) || !isFinite(revenueValue)) {
          console.error('[UET] Invalid revenue value:', revenue);
          return;
        }

        // Push detailed event matching old implementation
        window.uetq.push('event', 'Purchase-GTM', {
          'event_category': 'Ecommerce',
          'event_label': '',
          'event_value': 1,
          'revenue_value': revenueValue,
          'currency': currency,
        });
        console.log('[UET] Purchase tracked:', { revenue: revenueValue, currency });
      } else {
        console.warn('[UET] uetq not available');
      }
    },
    []
  );

  return {
    trackEvent,
    trackPageView,
    trackPurchase,
    trackUetEvent,
    trackUetPurchase,
  };
};
